import "core-js/modules/es.array.reverse.js";
var _dec, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import "core-js/modules/web.dom-collections.iterator.js";
import { isEqual } from "lodash-es";
import { action, computed, observable, reaction, runInAction, untracked, makeObservable } from "mobx";
import { fromPromise, FULFILLED } from "mobx-utils";
import CesiumEvent from "terriajs-cesium/Source/Core/Event";
import Rectangle from "terriajs-cesium/Source/Core/Rectangle";
import CatalogMemberMixin from "../ModelMixins/CatalogMemberMixin";
import CameraView from "../Models/CameraView";
import NoViewer from "../Models/NoViewer";
import ViewerMode, { getViewerType } from "../Models/ViewerMode";

// Async loading of Leaflet and Cesium

const leafletFromPromise = computed(() => fromPromise(import("../Models/Leaflet").then(Leaflet => Leaflet.default)), {
  keepAlive: true
});
const cesiumFromPromise = computed(() => fromPromise(import("../Models/Cesium").then(Cesium => Cesium.default)), {
  keepAlive: true
});

// Viewer options. Designed to be easily serialisable

const viewerOptionsDefaults = {
  useTerrain: true
};
/**
 * A class that deals with initialising, destroying and switching between viewers
 * Each map-view should have it's own TerriaViewer (main viewer, preview map, etc.)
 */
let TerriaViewer = (_dec = computed({
  keepAlive: true
}), _class = class TerriaViewer {
  get baseMap() {
    return this._baseMap;
  }

  /**
   * Returns the basemap that is currently loading
   */
  get loadingBaseMap() {
    return this._loadingBaseMap;
  }
  async setBaseMap(baseMap) {
    if (!baseMap) return;
    runInAction(() => {
      this._loadingBaseMap = baseMap;
    });
    try {
      const result = await baseMap.loadMapItems();
      if (result.error) {
        var _ref;
        result.raiseError(this.terria, {
          title: {
            key: "models.terria.loadingBaseMapErrorTitle",
            parameters: {
              name: (_ref = CatalogMemberMixin.isMixedInto(baseMap) ? baseMap.name : baseMap.uniqueId) !== null && _ref !== void 0 ? _ref : "Unknown item"
            }
          }
        });
      } else {
        runInAction(() => {
          // Concurrent attempts to load basemap might not complete in the same
          // order they were called. Set as current basemap only if this was
          // the last call to setBaseMap.
          if (this._loadingBaseMap === baseMap) {
            // If the basemap specifies a preferred viewer mode, switch to it.
            if (baseMap.preferredViewerMode) {
              var _getViewerType;
              this.viewerMode = (_getViewerType = getViewerType(baseMap.preferredViewerMode)) !== null && _getViewerType !== void 0 ? _getViewerType : this.viewerMode;
            }
            this._baseMap = baseMap;
          }
        });
      }
    } finally {
      // Unset loadingBaseMap
      if (this._loadingBaseMap === baseMap) {
        runInAction(() => {
          this._loadingBaseMap = undefined;
        });
      }
    }
  }

  /**
   * Switch to a base map that is compatible with the current viewer
   *
   * @returns A promise that yields `true` if the switch was made.
   */
  async useViewerCompatibleBaseMap() {
    var _this$baseMap, _this$terria$baseMaps;
    const currentViewerType = this.viewerMode;
    const baseMapViewerType = (_this$baseMap = this.baseMap) !== null && _this$baseMap !== void 0 && _this$baseMap.preferredViewerMode ? getViewerType(this.baseMap.preferredViewerMode) : undefined;
    if (!baseMapViewerType || baseMapViewerType === currentViewerType) {
      return false;
    }

    // Select a base map that either does not require a specific viewer mode or
    // specifies a compatible mode.
    const compatibleBaseMap = (_this$terria$baseMaps = this.terria.baseMapsModel.baseMapItems.find(it => !it.item.preferredViewerMode || getViewerType(it.item.preferredViewerMode) === currentViewerType)) === null || _this$terria$baseMaps === void 0 ? void 0 : _this$terria$baseMaps.item;
    return compatibleBaseMap ? this.setBaseMap(compatibleBaseMap).then(() => true) : false;
  }

  // This is a "view" of a workbench/other

  get homeCamera() {
    return this._homeCamera;
  }
  set homeCamera(cameraView) {
    if (isEqual(this._homeCamera.rectangle, Rectangle.MAX_VALUE)) {
      this.currentViewer.zoomTo(cameraView, 0.0);
    }
    this._homeCamera = cameraView;
  }
  constructor(terria, items) {
    _defineProperty(this, "terria", void 0);
    _initializerDefineProperty(this, "_baseMap", _descriptor, this);
    /**
     * Tracks the basemap that is currently being loaded
     */
    _initializerDefineProperty(this, "_loadingBaseMap", _descriptor2, this);
    _defineProperty(this, "items", void 0);
    _initializerDefineProperty(this, "viewerMode", _descriptor3, this);
    // Set by UI
    _initializerDefineProperty(this, "viewerOptions", _descriptor4, this);
    // Disable all mouse (& keyboard) interaction
    _initializerDefineProperty(this, "disableInteraction", _descriptor5, this);
    _defineProperty(this, "_homeCamera", new CameraView(Rectangle.MAX_VALUE));
    _initializerDefineProperty(this, "mapContainer", _descriptor6, this);
    /**
     * The distance between two pixels at the bottom center of the screen.
     * Set in lib/ReactViews/Map/Legend/DistanceLegend.jsx
     */
    _initializerDefineProperty(this, "scale", _descriptor7, this);
    _defineProperty(this, "beforeViewerChanged", new CesiumEvent());
    _defineProperty(this, "afterViewerChanged", new CesiumEvent());
    _defineProperty(this, "_lastViewer", void 0);
    _defineProperty(this, "viewerChangeTracker", undefined);
    makeObservable(this);
    this.terria = terria;
    this.items = items;
    if (!this.viewerChangeTracker) {
      this.viewerChangeTracker = reaction(() => this.currentViewer, () => {
        this.afterViewerChanged.raiseEvent();
      });
    }
  }
  get attached() {
    return this.mapContainer !== undefined;
  }
  /**
   * Promise for async loading of current `viewerMode`
   * Starts when TerriaViewer is attached to a div and `viewerMode` is set
   */
  get viewerLoadPromise() {
    return Promise.resolve(this._currentViewerConstructorPromise).then(() => {});
  }

  /**
   * Get a mobx-utils promise to a constructor for currentViewer. Start loading
   * Leaflet or Cesium depending on `viewerMode` if attached to a div
   */
  get _currentViewerConstructorPromise() {
    let viewerFromPromise = fromPromise.resolve(NoViewer);
    if (this.attached && this.viewerMode === ViewerMode.Leaflet) {
      viewerFromPromise = leafletFromPromise.get();
    } else if (this.attached && this.viewerMode === ViewerMode.Cesium) {
      viewerFromPromise = cesiumFromPromise.get();
    }
    return viewerFromPromise;
  }
  get currentViewer() {
    // Use untracked on everything to ensure the viewer isn't recreated
    //  except when the viewer is required to change, the currently required
    //  viewer class finishes loading from an async chunk or the map container
    //  is changed

    const currentView = untracked(() => this.destroyCurrentViewer());
    let newViewer;
    try {
      // If a div is attached and a viewer is ready, use it
      if (this.attached && this._currentViewerConstructorPromise.state === FULFILLED) {
        const SomeViewer = this._currentViewerConstructorPromise.value;
        newViewer = untracked(() => new SomeViewer(this, this.mapContainer));
      } else {
        newViewer = untracked(() => new NoViewer(this));
      }
    } catch (error) {
      // Switch viewerMode inside computed. Could change viewers to
      //  guarantee no throw in constructor and instead have a `start()`
      //  method that can throw. Then call that `start()` method inside
      //  a reaction (reaction would also deal with viewer fallback).
      // Using this approach might remove the need for `untracked`
      setTimeout(action(() => {
        this.terria.raiseErrorToUser(error);
        this.viewerMode = this.viewerMode === ViewerMode.Cesium ? ViewerMode.Leaflet : undefined;
      }), 0);
      newViewer = untracked(() => new NoViewer(this));
    }
    this._lastViewer = newViewer;
    newViewer.setInitialView(currentView || untracked(() => this.homeCamera));
    return newViewer;
  }

  // Pull out attaching logic into it's own step. This allows constructing a TerriaViewer
  // before its UI element is mounted in React to set basemap, items, viewermode
  attach(mapContainer) {
    this.mapContainer = mapContainer;
  }
  detach() {
    // Detach from a container
    this.mapContainer = undefined;
    this.destroyCurrentViewer();
  }
  destroy() {
    var _this$viewerChangeTra;
    this.detach();
    (_this$viewerChangeTra = this.viewerChangeTracker) === null || _this$viewerChangeTra === void 0 || _this$viewerChangeTra.call(this);
  }
  destroyCurrentViewer() {
    let currentView;
    if (this._lastViewer !== undefined) {
      this.beforeViewerChanged.raiseEvent();
      currentView = this._lastViewer.getCurrentCameraView();
      this._lastViewer.destroy();
      this._lastViewer = undefined;
    }
    return currentView;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "_baseMap", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "_loadingBaseMap", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "viewerMode", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return ViewerMode.Cesium;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "viewerOptions", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return viewerOptionsDefaults;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "disableInteraction", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class.prototype, "homeCamera", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "homeCamera"), _class.prototype), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "mapContainer", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "scale", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 1;
  }
}), _applyDecoratedDescriptor(_class.prototype, "viewerLoadPromise", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "viewerLoadPromise"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "_currentViewerConstructorPromise", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "_currentViewerConstructorPromise"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "currentViewer", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "currentViewer"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "attach", [action], Object.getOwnPropertyDescriptor(_class.prototype, "attach"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "detach", [action], Object.getOwnPropertyDescriptor(_class.prototype, "detach"), _class.prototype), _class);
export { TerriaViewer as default };